import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Portal,
  Spinner,
  Text,
  VStack,
  useOutsideClick,
} from '@chakra-ui/react';
import { MdClose, MdPersonSearch } from 'react-icons/md';
import { AppInput } from '../../../../components/ui';
import { listAppUsers } from '../../../../repositories/appUser.repository';

function personLabel(p) {
  return (
    p?.fullName ||
    p?.tradeName ||
    p?.ownerName ||
    [p?.firstName, p?.lastName].filter(Boolean).join(' ') ||
    p?.phone ||
    (p?.id != null ? `User #${p.id}` : '')
  );
}

/**
 * Smart referred-by picker:
 * - Type to search members (name / phone / village)
 * - Click a result to link the user
 * - Or keep free text if the referrer is not in the system
 * Dropdown is portaled so modal/table overflow does not clip it.
 */
export function ReferredByPicker({
  label = 'Referred by (optional)',
  value = { appUserId: null, name: '' },
  onChange,
  excludeUserId = null,
  helperText = 'Search a user, or type a name if they are not on Utkarsh yet.',
}) {
  const [query, setQuery] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [menuPos, setMenuPos] = useState(null);
  const wrapRef = useRef(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const debounceRef = useRef(null);

  useOutsideClick({
    ref: wrapRef,
    handler: (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    },
  });

  const updateMenuPos = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferUp = spaceBelow < 240 && rect.top > spaceBelow;
    const width = Math.max(rect.width, 240);
    setMenuPos({
      top: preferUp ? undefined : rect.bottom + 4,
      bottom: preferUp ? window.innerHeight - rect.top + 4 : undefined,
      left: Math.min(rect.left, window.innerWidth - width - 8),
      width,
      maxH: Math.min(260, preferUp ? rect.top - 12 : spaceBelow - 12),
    });
  }, []);

  useEffect(() => {
    setQuery(value?.name || '');
  }, [value?.appUserId, value?.name]);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return undefined;
    }
    updateMenuPos();
    const onScrollOrResize = () => updateMenuPos();
    window.addEventListener('resize', onScrollOrResize);
    // Capture scroll from modal body / nested overflow parents
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, query, results.length, loading, updateMenuPos]);

  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open || q.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await listAppUsers({ search: q, pageSize: 12 });
        const items = (res.items || []).filter(
          (p) =>
            excludeUserId == null || Number(p.id) !== Number(excludeUserId),
        );
        setResults(items);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, excludeUserId]);

  const linked = value?.appUserId != null;
  const showMenu = open && query.trim().length >= 2 && menuPos;

  const emit = (next) => {
    onChange?.({
      appUserId: next.appUserId ?? null,
      name: String(next.name || '').slice(0, 200),
    });
  };

  const onType = (e) => {
    const text = e.target.value;
    setQuery(text);
    setOpen(true);
    emit({ appUserId: null, name: text });
  };

  const selectPerson = (p) => {
    const name = personLabel(p);
    setQuery(name);
    setOpen(false);
    emit({ appUserId: p.id, name });
  };

  const clear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    emit({ appUserId: null, name: '' });
  };

  const hint = useMemo(() => {
    if (linked) return `Linked user #${value.appUserId}`;
    if (query.trim()) return 'Free-text referrer (not linked to a user)';
    return helperText;
  }, [linked, value?.appUserId, query, helperText]);

  return (
    <Box ref={wrapRef}>
      <Flex gap={2} align="end">
        <Box ref={anchorRef} flex="1" minW={0}>
          <AppInput
            label={label}
            value={query}
            onChange={onType}
            onFocus={() => {
              setOpen(true);
              requestAnimationFrame(updateMenuPos);
            }}
            maxLength={200}
            placeholder="Search name / phone, or type freely…"
            h="44px"
            size="md"
          />
        </Box>
        {query ? (
          <IconButton
            aria-label="Clear referred by"
            icon={<MdClose />}
            size="md"
            variant="ghost"
            mb={0.5}
            onClick={clear}
          />
        ) : (
          <Box color="text.secondary" mb={2} px={1}>
            <MdPersonSearch size={22} />
          </Box>
        )}
      </Flex>
      <Text fontSize="11px" color={linked ? 'green.600' : 'text.secondary'} mt={1}>
        {hint}
      </Text>

      {showMenu ? (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            zIndex={2000}
            top={menuPos.top != null ? `${menuPos.top}px` : undefined}
            bottom={menuPos.bottom != null ? `${menuPos.bottom}px` : undefined}
            left={`${menuPos.left}px`}
            w={`${menuPos.width}px`}
            maxH={`${Math.max(120, menuPos.maxH)}px`}
            overflowY="auto"
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            boxShadow="lg"
          >
            {loading ? (
              <Flex align="center" gap={2} p={3}>
                <Spinner size="sm" />
                <Text fontSize="13px" color="text.secondary">
                  Searching members…
                </Text>
              </Flex>
            ) : results.length ? (
              <VStack align="stretch" spacing={0}>
                {results.map((p) => (
                  <Box
                    key={p.id}
                    as="button"
                    type="button"
                    textAlign="left"
                    px={3}
                    py={2}
                    _hover={{ bg: 'bg.amberTint' }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectPerson(p)}
                  >
                    <Text fontSize="13px" fontWeight="600">
                      {personLabel(p)}
                    </Text>
                    <Text fontSize="12px" color="text.secondary">
                      {[p.phone, p.village || p.villageName, p.userTypeLabel]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box p={3}>
                <Text fontSize="13px" color="text.secondary">
                  No member match — keep typing to save as free-text name.
                </Text>
              </Box>
            )}
          </Box>
        </Portal>
      ) : null}
    </Box>
  );
}
